<template>
  <div class="b-tabs" :class="mainClasses">
    <nav class="tabs" :class="navClasses" @keydown="manageTablistKeydown">
      <slot name="start" />
      <ul
        :aria-orientation="vertical ? 'vertical' : 'horizontal'"
        role="tablist"
      >
        <li
          v-for="(childItem, childIdx) in items"
          :key="childItem.value"
          v-show="childItem.visible"
          :class="[
            childItem.headerClass,
            {
              'is-active': childItem.isActive,
              'is-disabled': childItem.disabled
            }
          ]"
          role="tab"
          :aria-controls="`${childItem.value}-content`"
          :aria-selected="`${childItem.isActive}`"
        >
          <b-slot-component
            ref="tabLink"
            v-if="childItem.$scopedSlots.header"
            :component="childItem"
            name="header"
            tag="a"
            :id="`${childItem.value}-label`"
            :tabindex="childItem.isActive ? 0 : -1"
            @focus.native="currentFocus = childIdx"
            @click.native="childClick(childItem)"
            @keydown="manageTabKeydown($event, childItem)"
          />
          <b-slot-component
            ref="tabLink"
            v-else-if="childItem.$scopedSlots.headerClosable"
            :component="childItem"
            name="headerClosable"
            tag="a"
            :id="`${childItem.value}-label`"
            :tabindex="childItem.isActive ? 0 : -1"
            style="padding: 5px 0px 5px 16px"
            @focus.native="currentFocus = childIdx"
            @click.native="childClick(childItem)"
            @keydown="manageTabKeydown($event, childItem)"
          />
          <a
            ref="tabLink"
            v-else
            :id="`${childItem.value}-label`"
            :tabindex="childItem.isActive ? 0 : -1"
            @focus="currentFocus = childIdx"
            @click="childClick(childItem)"
            @keydown="manageTabKeydown($event, childItem)"
          >
            <b-icon
              v-if="childItem.icon"
              :icon="childItem.icon"
              :pack="childItem.iconPack"
              :size="size"
            />
            <span>{{ childItem.label }}</span>
          </a>
        </li>
        <li class="add-tab">
          <slot name="addTab" />
        </li>
      </ul>
      <slot name="end" />
    </nav>
    <section
      class="tab-content"
      :class="{ 'is-transitioning': isTransitioning }"
    >
      <slot />
    </section>
  </div>
</template>

<script>
/* eslint-disable */

import TabbedMixin from './TabbedMixin.js'

export default {
  name: 'MTabs',
  mixins: [TabbedMixin('tab')],
  props: {
    expanded: {
      type: Boolean,
      default: () => {
        return false
      }
    },
    type: {
      type: [String, Object],
      default: () => {
        return null
      }
    },
    animated: {
      type: Boolean,
      default: () => {
        return true
      }
    },
    multiline: Boolean
  },
  data() {
    return {
      currentFocus: this.value
    }
  },
  computed: {
    mainClasses() {
      return {
        'is-fullwidth': this.expanded,
        'is-vertical': this.vertical,
        'is-multiline': this.multiline,
        [this.position]: this.position && this.vertical
      }
    },
    navClasses() {
      return [
        this.type,
        this.size,
        {
          [this.position]: this.position && !this.vertical,
          'is-fullwidth': this.expanded,
          'is-toggle': this.type === 'is-toggle-rounded'
        }
      ]
    }
  },
  methods: {
    giveFocusToTab(tab) {
      if (tab.$el && tab.$el.focus) {
        tab.$el.focus()
      } else if (tab.focus) {
        tab.focus()
      }
    },
    manageTablistKeydown(event) {
      // https://developer.mozilla.org/fr/docs/Web/API/KeyboardEvent/key/Key_Values#Navigation_keys
      const { key } = event
      switch (key) {
        case this.vertical ? 'ArrowUp' : 'ArrowLeft':
        case this.vertical ? 'Up' : 'Left': {
          let prevIdx = this.getPrevItemIdx(this.currentFocus, true)
          if (prevIdx === null) {
            // We try to give focus back to the last visible element
            prevIdx = this.getPrevItemIdx(this.items.length, true)
          }
          if (
            prevIdx !== null &&
            this.$refs.tabLink &&
            prevIdx < this.$refs.tabLink.length &&
            !this.items[prevIdx].disabled
          ) {
            this.giveFocusToTab(this.$refs.tabLink[prevIdx])
          }
          event.preventDefault()
          break
        }
        case this.vertical ? 'ArrowDown' : 'ArrowRight':
        case this.vertical ? 'Down' : 'Right': {
          let nextIdx = this.getNextItemIdx(this.currentFocus, true)
          if (nextIdx === null) {
            // We try to give focus back to the first visible element
            nextIdx = this.getNextItemIdx(-1, true)
          }
          if (
            nextIdx !== null &&
            this.$refs.tabLink &&
            nextIdx < this.$refs.tabLink.length &&
            !this.items[nextIdx].disabled
          ) {
            this.giveFocusToTab(this.$refs.tabLink[nextIdx])
          }
          event.preventDefault()
          break
        }
      }
    },

    manageTabKeydown(event, childItem) {
      // https://developer.mozilla.org/fr/docs/Web/API/KeyboardEvent/key/Key_Values#Navigation_keys
      const { key } = event
      switch (key) {
        case ' ':
        case 'Space':
        case 'Spacebar':
        case 'Enter': {
          this.childClick(childItem)
          event.preventDefault()
          break
        }
      }
    }
  }
}
</script>
<style>
.add-tab {
  border-left: 1px solid rgb(219, 219, 219);
  margin-left: 5px;
}
</style>
